import * as ActivityText    from '../activity';
import * as Channels        from '../contacts/channels';
import { format }           from 'date-fns';
import { formatDistance }   from 'date-fns';
import { getFileURL }       from '../util/file_entry';
import { isSameDay }        from 'date-fns';
import { parseISO }         from 'date-fns';
import { subMilliseconds }  from 'date-fns';
import Badge                from '../components/badge';
import BroadlyAvatar        from '../components/broadly_avatar';
import classnames           from 'classnames';
import getFirstActivityText from '../contacts/get_first_activity_text';
import ImageZoom            from '../components/image_zoom';
import Immutable            from 'immutable';
import InitialsAvatar       from '../components/initials_avatar';
import Link                 from '../components/link';
import ProgressBar          from '../components/progress_bar';
import React                from 'react';
import Spinner              from '../components/spinner';
import StarRating           from '../components/star_rating';


// This component renders the activity/message history.
//
// This is a blueprint for some of the things we can do, but we're still working
// on the specific design, and guess this entire algorithm will change.
//
// Short summary of how it currently works:
//
// - It splits activities into sequences that are within 5 minutes of each
//   other.  So if we have activities at 11:02, 11:03 and 12:45, that would be
//   two sequences (11:02, 11:03 in one sequence, 12:45 in the other)
//
// - That way we can show the time once for a sequence of activities that
//   occurred in close proximity to each other.
//
// - Next, each sequence is split further into sequence of activities by the
//   same actor (typically, sender of the message).  So if Carly sent two
//   messages, and then Ronald sent another, those are two sequences.
//
// - That wey we can show a sequence of messages from the same person as one
//   continous unit (eg we can style the first and last speed bubbles a certain
//   way).
//
// We're probably not going to do it this way, but this was a fun experiment,
// and picked a few tricks using Immutable.js and high-order functions.
const ActivitiesContext = React.createContext();

export default class Activities extends React.PureComponent {
  constructor(props) {
    super(props);
    this.messages       = React.createRef();
    this.state          = {};
    this.scrollToBottom = this.scrollToBottom.bind(this);
  }

  static getDerivedStateFromProps(nextProps) {
    const { activities, conversation } = nextProps;

    const activitiesList              = Immutable.List(nextProps.activities);
    const activitiesWithCustomerAdded = activitiesList
      .insert(0, getContactAdded({ conversation, activities }));

    return { activitiesWithCustomerAdded };
  }

  scrollToBottom(retry = 0) {
    if (retry === 10)
      return;

    const element = this.messages.current;
    if (element) {
      setTimeout(() => {
        if (element.scrollHeight === 0)
          this.scrollToBottom(retry + 1);
        else
          element.scrollTop = element.scrollHeight;
      }, 60);
    }
  }

  componentDidMount() {
    this.scrollToBottom();
  }

  componentDidUpdate(prevProps) {
    const isNewConversation = prevProps.conversation.id !== this.props.conversation.id;
    const hasNewActivity    = prevProps.activities.length !== this.props.activities.length;

    if (isNewConversation || hasNewActivity)
      this.scrollToBottom();
  }

  render() {
    const { activities }                  = this.props;
    const { conversation }                = this.props;
    const { localFiles }                  = this.props;
    const { retrySendMessage }            = this.props;
    const { business }                    = this.props;
    const { activitiesWithCustomerAdded } = this.state;

    return (
      <ActivitiesContext.Provider value={{ business, localFiles, retrySendMessage, scrollToBottom: this.scrollToBottom }}>
        <div className="messages" ref={this.messages}>
          <NextFollowUp {...{ conversation }}/>
          {activities &&
            <SequenceByDay {...{ conversation, activities: activitiesWithCustomerAdded }}/>
          }
        </div>
      </ActivitiesContext.Provider>
    );
  }
}


// Splits activities into sequences based on day
function SequenceByDay({ conversation, activities }) {
  const oldestFirst = activities.sortBy(activity => activity.timestamp);
  const sequences   = sequenceBy(oldestFirst, byDay());
  return sequences
    .map((inSequence, i) => (
      <TimeSequence key={i} conversation={conversation} timestamp={inSequence[0].timestamp} activities={inSequence}/>
    ));
}

function byDay() {
  return function(previous, current) {
    return isSameDay(parseISO(current.timestamp), parseISO(previous.timestamp));
  };
}


// Render a squence of activities that occurred in the same timeframe
function TimeSequence({ timestamp, conversation, activities }) {
  return (
    <div>
      <ActivitiesGroupHeader {...{ timestamp }}/>
      <ActivityList key={activities[0].id} {...{ conversation, activities }}/>
    </div>
  );
}


// Shows the time of the next message
function ActivitiesGroupHeader({ timestamp }) {
  return (
    <div className="activities-group-header">
      {format(parseISO(timestamp), 'MMMM dd, yyyy')}
    </div>
  );
}


// Render a sequence of activities/messages
function ActivityList({ conversation, activities }) {
  return activities.map((activity, i) => {
    return (<ActivityItem key={i} {...{ conversation, activity }}/>);
  });
}

// Render an activity based on its type
function ActivityItem({ conversation, activity }) {
  const [ customer ] = conversation.customers;
  const { type }     = activity;
  const types        = {
    Ask,
    Call,
    ConversationActivity,
    CustomerAdded,
    Feedback,
    FollowUpMessage,
    IncomingMessage,
    InvoicePaid,
    InvoiceRefunded,
    InvoiceSent,
    OutgoingMessage,
    Review,
    Transaction
  };

  const ActivityType = types[type];
  if (ActivityType)
    return (<ActivityType customer={customer} activity={activity}/>);
  else
    return null;
}

function ConversationActivity({ activity }) {
  const body = ActivityText.getText({ activity });
  return (<div className="conversation-activity">{body}</div>);
}

function FollowUpMessage({ activity }) {
  const { timestamp } = activity;
  const { message }   = activity;
  const { status }    = message;
  const { user }      = message;
  const { channel }   = message;
  const body          = ActivityText.getFollowUpMessageText({ activity });
  const askType       = message?.ask?.type ?? 'missing';
  const messageName   = message.name;
  const campaignID    = message.campaign;
  const { subject }   = message;

  const name = user?.name;

  if (askType === 'classify' || askType === 'review')
    return <ReviewRequest {...{ body, timestamp, status, name, channel }}/>;
  else if (messageName === 'campaign')
    return <CampaignMessage type="sent" {...{ ...message, subject, campaignID, timestamp }}/>;
  else
    return <Message {...{ body, timestamp, status, name, channel }} type="sent"/>;
}

function ReviewRequest({ body, timestamp, status, name, channel }) {
  return (
    <React.Fragment>
      {body && (
        <MessageContainer type="sent" {...{ timestamp, status, name, channel }} className="message-review-requested">
          <div className="message-text">
            <div className="icon icons8-very-popular-topic message-review-request__icon" title="Review Request Sent"></div>
            <div>
              <div className="message-review-request__heading">{body}</div>
            </div>
          </div>
        </MessageContainer>
      )}
    </React.Fragment>
  );
}


function Ask({ activity }) {
  const { type } = activity.ask;
  const body     = ActivityText.getAskText({ activity });

  if (type === 'feedback')
    return null;

  return (
    <div className="activity-ask">{body}</div>
  );
}


function Review({ customer, activity }) {
  const { review }      = activity;
  const { outdated }    = review;
  const hasProviders    = review?.providers?.length > 0;
  const providerName    = hasProviders && review.providers[0].name;
  const transactionDate = review?.transactions?.[0]?.timestamp;

  return (
    <div className="message-received">
      <div className="message-content">
        <InitialsAvatar name={customer.name.full}/>
        <div className="message-review message-bubble-and-status">
          <div className="message-bubble">
            <div className="message-text">
              <div className="message-review__header">
                <StarRating rating={review.rating}/>
                <span className="message-review__title">They left a {review.rating} star review <Link href={review.sourceURL} external>on {capitalizeFirstLetter(review.service)}</Link></span>
              </div>
              {review.body && (
                <p className="message-review__body">&quot;{review.body}&quot;</p>
              )}
              {hasProviders && transactionDate && (
                <div className="message-review__provider">Service provided on {format(parseISO(transactionDate), 'MM/dd/yyyy')} by {providerName}</div>
              )}
            </div>
          </div>
          {outdated && (
            <div className="message-status review-outdated">Google may have removed this review from your listing. <Link href="https://broadlyhelp.force.com/s/article/Why-Google-removes-reviews" external>Learn&nbsp;more</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function Feedback({ activity, customer }) {
  const { feedback } = activity;
  const { message }  = feedback;
  const { promoter } = feedback;

  const feedbackAvatarClass = classnames({
    'customer-avatar customer-avatar--promoter':  promoter,
    'customer-avatar customer-avatar--detractor': !promoter
  });

  return (
    <React.Fragment>
      <div className="message-feedback">
        <div className="message-content">
          <div className={feedbackAvatarClass}/>
          <div className="message-bubble-and-status">
            <div className="message-bubble">
              <div className="message-text">
                {promoter && 'Would recommend'}
                {!promoter && 'Would not recommend'}
              </div>
            </div>
          </div>
        </div>
      </div>
      {message && (
        <FeedbackMessage activity={activity} customer={customer}/>
      )}
    </React.Fragment>
  );
}


function FeedbackMessage({ activity, customer }) {
  const { feedback, timestamp } = activity;
  const name                    = customer.name.full;
  const { message }             = feedback;
  const { promoter }            = feedback;

  return (
    <div className="message-received">
      <div className="message-content">
        <InitialsAvatar name={name}/>
        <div className="message-bubble-and-status">
          <div className="message-bubble">
            <div className="message-text">{message.body}</div>
            {!promoter && (
              <div className="message-text">
                <hr/>We recommend you reach out to the customer quickly.
              </div>
            )}
            <div className="message-timestamp">{format(parseISO(timestamp), 'h:mm a')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}


function Transaction({ activity }) {
  const body = ActivityText.getTransactionText({ activity });
  return <div className="activity-transaction">{body}</div>;
}


function IncomingMessage({ customer, activity }) {
  const { timestamp }   = activity;
  const { message }     = activity;
  const { attachments } = message;
  const { body }        = message;
  const { channel }     = message;
  const name            = customer.name.full;

  return <Message {...{ body, timestamp, name, attachments, channel }} type='received'/>;
}


function InvoicePaid({ customer, activity }) {
  const { invoice }     = activity;
  const { description } = invoice;
  const { id }          = invoice;
  const { message }     = activity;
  const name            = customer.name.full;
  const { status }      = message;
  const { channel }     = message;
  const { payURL }      = invoice;
  const { mode }        = invoice;
  const { timestamp }   = activity;
  const headline        = ActivityText.getInvoicePaidText({ activity });

  return <Invoice {...{
    headline,
    description,
    action: 'View Receipt',
    id,
    timestamp,
    type:   'received',
    status,
    channel,
    name,
    payURL,
    mode
  }}/>;
}


function InvoiceRefunded({ customer, activity }) {
  const { invoice }     = activity;
  const { description } = invoice;
  const { id }          = invoice;
  const { message }     = activity;
  const name            = customer.name.full;
  const { status }      = message;
  const { channel }     = message;
  const { payURL }      = invoice;
  const { mode }        = invoice;
  const { timestamp }   = activity;
  const headline        = ActivityText.getInvoiceRefundedText({ activity });

  return <Invoice {...{
    headline,
    description,
    action: 'View Refund',
    id,
    timestamp,
    type:   'sent',
    status,
    channel,
    name,
    payURL,
    mode
  }}/>;
}


function InvoiceSent({ activity }) {
  const { invoice }     = activity;
  const { description } = invoice;
  const { id }          = invoice;
  const { message }     = activity;
  const { status }      = message;
  const { channel }     = message;
  const { timestamp }   = activity;
  const { payURL }      = invoice;
  const { user }        = invoice;
  const { mode }        = invoice;
  const { name }        = user;
  const headline        = ActivityText.getInvoiceSentText({ activity });

  return <Invoice {...{
    headline,
    description,
    action: 'View Invoice',
    id,
    timestamp,
    type:   'sent',
    status,
    channel,
    name,
    payURL,
    mode
  }}/>;
}


function Invoice({ headline, description, action, id, timestamp, type, name, status, payURL, mode }) {
  /* eslint-disable react/jsx-no-target-blank */
  return (
    <ActivitiesContext.Consumer>
      {({ business }) => {
        const businessID = business?.id;
        const isSent     = type === 'sent';
        const isLiveMode = mode === 'live';
        const href       = isSent ? `/${businessID}/metrics/invoices/${id}` : payURL;

        return (
          <MessageContainer {...{ id, type, name, timestamp, status }} className="message-invoice">
            <div className="message-text">
              <div className={`message-invoice__icon message-invoice__icon--${type}`}>
              </div>
              <div>
                <div className="message-invoice__heading">{headline}</div>
                <div className="message-invoice__subheading">{description}</div>
                <Link href={href} className="message-invoice__link" external={!isSent}>{!isLiveMode && (<Badge {...{ color: 'orange', text: 'Test' }}/>)}{action}</Link>
              </div>
            </div>
          </MessageContainer>
        );
      }}
    </ActivitiesContext.Consumer>
  );
  /* eslint-enabled react/jsx-no-target-blank */
}


function OutgoingMessage({ activity }) {
  const { timestamp } = activity;
  const { message }   = activity;
  const name          = message.user && message.user.name;

  return <Message {...{ ...message, timestamp, name }} type='sent'/>;
}


function CampaignMessage({ id, subject, campaignID, timestamp }) {
  return (
    <ActivitiesContext.Consumer>
      {({ business }) => {
        const businessID = business?.id;
        const href       = `/${businessID}/campaigns/${campaignID}`;

        return (
          <MessageContainer type="sent" {...{ id, campaignID, name, timestamp, status }} className="message-campaign">
            <div className="message-text">
              <div>
                <span className="message-campaign__title">Sent <Link className="message-campaign__link" href={href}>campaign</Link></span>
                <div className="message-campaign__subject">{subject}</div>
              </div>
            </div>
          </MessageContainer>
        );
      }}
    </ActivitiesContext.Consumer>
  );
}


// This is a message, sent or received.
// First message shows the sender's name, and last message
// shows the "tail".
function Message(message) {
  const { body }        = message;
  const { attachments } = message;

  return (
    <React.Fragment>
      {body && (
        <MessageContainer {...message} >
          <div className="message-text">{body}</div>
        </MessageContainer>
      )}
      {attachments && (
        <ActivitiesContext.Consumer>
          {({ localFiles }) => (
            attachments.map((attachment, key) => {
              const nativeURL = localFiles[attachment.fileID] ||
                getFileURL(attachment.nativeURL);
              return (
                <MessageContainer key={key} {...message} >
                  <Attachment {...attachment} nativeURL={nativeURL}/>
                </MessageContainer>
              );
            })
          )}
        </ActivitiesContext.Consumer>
      )}
    </React.Fragment>
  );
}

function MessageContainer({ id, type, name, timestamp, status, channel, children, className }) {
  const isOutgoingMessage = (type === 'sent');
  const isSentByBroadly   = (isOutgoingMessage && !name);
  const isPendingInQueue  = (status === 'inQueuePending');
  const opacity           = isPendingInQueue ? '.7' : '1';
  const messageClassName  = classnames(`message-${type}`, className);
  const channelLabel      = (channel && `${Channels.channelToLabel(channel)} – `) || '';

  return (
    <div className={messageClassName} style={{ opacity }}>
      <div className="message-content">
        {isSentByBroadly ? <BroadlyAvatar/> : <InitialsAvatar name={name}/>}
        <div className="message-bubble-and-status">
          <div className="message-bubble">
            {children}
            <div className="message-timestamp">{channelLabel}{format(parseISO(timestamp), 'h:mm a')}</div>
          </div>
          {type === 'sent' &&
          <MessageStatus messageId={id} status={status}/>}
        </div>
      </div>
    </div>
  );
}

function Attachment(attachment) {
  if (isImage(attachment))
    return <ImageAttachment attachment={attachment}/>;
  else
    return <FileAttachment attachment={attachment}/>;
}

function isImage(attachment) {
  // When the attachment has nativeURL, is because the user
  // picked it from the photo gallery
  if (attachment.nativeURL)
    return true;
  else {
    const mimeType = (attachment.versions && attachment.versions.original && attachment.versions.original.mimeType) || attachment.mimeType;
    return /image\/*./.test(mimeType);
  }
}

function FileAttachment({ attachment }) {
  const { progress } = attachment;
  const { versions } = attachment;

  const href = versions && versions.original.url;

  return (
    <div className="activity-file-attachment">
      <Link href={href} external>
        <i className="f7-icons attachment-icon-preview">document</i>
        {progress && <span>Loading...</span>}
        {!progress && <span>View Attachment</span>}
      </Link>
      {progress && <ProgressBar progress={progress}/>}
    </div>
  );
}

function ImageAttachment({ attachment }) {
  const { nativeURL } = attachment;
  const { progress }  = attachment;
  const { versions }  = attachment;

  const isLocalImage  = !!nativeURL;
  const isRemoteImage = versions && versions.medium;

  if (isLocalImage || isRemoteImage) {
    const src      = isRemoteImage ? versions.medium.url : getFileURL(nativeURL);
    const width    = isRemoteImage ? `${versions.medium.width}px` : '100%';
    const style    = { width, maxWidth: '100%' };
    const imageURL = isRemoteImage ? getLargeImage(versions) : getFileURL(nativeURL);

    return (
      <ImageZoom image={imageURL}>
        {({ open }) =>
          <a onClick={open}>
            <ActivitiesContext.Consumer>
              {({ scrollToBottom }) => (
                <div style={{ position: 'relative' }}>
                  <img src={src} style={style} onLoad={scrollToBottom}/>
                  {progress && <ProgressBar progress={progress}/>}
                </div>
              )}
            </ActivitiesContext.Consumer>
          </a>
        }
      </ImageZoom>
    );
  } else
    return <ImagePlaceholder/>;
}

function ImagePlaceholder() {
  return (
    <div className="message-image-placeholder">
      <span>
        <i className="icons8 icons8-image message-attachment-loading__icon"></i> Loading...
      </span>
    </div>
  );
}

function MessageStatus({ messageId, status }) {
  switch (status) {
    case 'inQueuePending':
      return (
        <div className="message-status message-queue-pending">
          <Spinner/>
        </div>
      );
    case 'inQueueError':
      return (
        <ActivitiesContext.Consumer>
          {({ retrySendMessage }) => (
            <div className="message-status message-undelivered" onClick={retrySendMessage(messageId)}>
              <div className="message-undelivered__icon">
                <i className="f7-icons refresh-icon">refresh_round</i>
              </div>
              <div className="message-undelivered__text">Retry</div>
            </div>
          )}
        </ActivitiesContext.Consumer>
      );
    case 'undelivered':
    case 'failed':
    case 'blocked':
      return (
        <div className="message-status message-undelivered">
          <div className="message-undelivered__icon">
            <i className="f7-icons alert-icon">alert</i>
          </div>
          <div className="message-undelivered__text">Not Delivered</div>
        </div>
      );
    default:
      return null;
  }
}

function getLargeImage(versions) {
  if (versions.large)
    return versions.large.url;
  else
    return versions.medium.url;
}

function CustomerAdded({ customer }) {
  const body = getFirstActivityText({ customer });
  return <div className="activity-customer-added">{body}</div>;
}

function getContactAdded({ conversation, activities }) {
  const [ customer ] = conversation?.customers;
  const timestamp    = customer.customerSince || customer.createdAt;

  // Modify timestamp so customer added activity comes before first transaction.
  const timestampForCustomerAdded = subMilliseconds(parseISO(timestamp), 1);
  return {
    type:      'CustomerAdded',
    activities,
    timestamp: timestampForCustomerAdded.toISOString()
  };
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase().concat(string.slice(1));
}


// Given a collection of items, returns a collection of collections, each a
// consecutive sequence of elements, as determined by the predicate.  Retains
// the order of the source collection.
//
// For example:
//  sequenceBy([1, 1, 1, 2, 5, 5], (a, b)=> a === b)
//  => [ [1, 1, 1], [2], [5, 5] ]
//
//  sequences = sequenceBy(original, predicate);
//  assert.deepEqual( flatten(sequences), original);
function sequenceBy(collection, predicate) {
  return collection.reduce(function(all, item) {
    const last = all[all.length - 1];
    const same = last && predicate(last[0], item);
    if (same)
      last.push(item);
    else
      all.push([ item ]);
    return all;
  }, []);
}


function NextFollowUp({ conversation }) {
  const [ customer ]   = conversation.customers;
  const hasNextMessage = !!(customer.nextMessage && customer.nextMessage.time);
  const isUnsubscribed = (customer.contact.subscribed === false);

  if (hasNextMessage)
    return <NextMessage time={customer.nextMessage.time}/>;
  else if (isUnsubscribed)
    return <Unsubscribed/>;
  else
    return null;
}


function NextMessage({ time }) {
  return (
    <div className="activities-group-header">
      <i className="messages__follow-up-icon icon f7-icons">info_round</i>
      <span>We will follow up in {formatDistance(new Date(), parseISO(time))}</span>
    </div>
  );
}


function Unsubscribed() {
  return (
    <div className="activities-group-header">
      <i className="messages__follow-up-icon icon f7-icons">info_round</i>
      <span>Contact unsubscribed</span>
    </div>
  );
}

function secondsToHms(seconds) {
  const secondsNumber = Number(seconds);
  const h             = Math.floor(secondsNumber / 3600);
  const m             = Math.floor(secondsNumber % 3600 / 60);
  const s             = Math.floor(secondsNumber % 3600 % 60);

  const hDisplay = h > 0 ? `${h} ${h > 1 ? 'hours' : 'hour'}` : '';
  const mDisplay = m > 0 ? `${m} ${m > 1 ? 'minutes' : 'minute'}` : '';
  const sDisplay = s > 0 ? `${s} ${s > 1 ? 'seconds' : 'second'}` : '';

  return `${hDisplay} ${mDisplay} ${sDisplay}`.trim();
}

function getCallText({ call }) {
  const { duration } = call;
  const { status }   = call;

  if (call.status === 'completed')
    return secondsToHms(duration);
  else
    return capitalizeFirstLetter(status).replace(/-/g, ' ');
}

function Call({ activity, customer }) {
  const { call }      = activity;
  const { direction } = call;
  const { user }      = call;
  const { timestamp } = activity;
  const isOutgoing    = direction === 'outbound';

  return (
    <div className={`message-${direction}-call`}>
      <div className="message-content">
        <ActivitiesContext.Consumer>
          {({ business }) => {
            if (business) {
              const name = isOutgoing ? (user && user.name) || business.name : customer.name.full;
              return <InitialsAvatar name={name}/>;
            } else
              return null;
          }}
        </ActivitiesContext.Consumer>
        <div className="message-bubble-and-status">
          <div className="message-bubble">
            <div className="message-text">
              <i className="f7-icons">phone</i>
              {getCallText({ call })}
            </div>
            <div className="message-timestamp">{format(parseISO(timestamp), 'h:mm a')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
